import React, { useState, useEffect } from 'react';
import Footer from "../components/Footer";

import { useTranslation } from 'react-i18next';
import SEOHead from "../components/SEOHead";

const AUTOMATION_EXAMPLE = {
  name: 'Order Status Auto-Responder',
  trigger: "Message contains 'pedido' OR 'order'",
  steps: [
    { type: 'trigger', label: 'Customer sends message', detail: '"Where is my order #12345?"' },
    { type: 'condition', label: 'Check order status', detail: 'Query order API for #12345' },
    { type: 'action', label: 'Send response', detail: 'Shipped via FedEx, arrives June 12' },
    { type: 'action', label: 'Log interaction', detail: 'Saved to CRM + analytics' },
  ],
};

function AutomationsDemo() {
    const { t } = useTranslation();
const [activeStep, setActiveStep] = useState(0);

  const templates = [
    { name: 'Welcome New User', trigger: 'New signup', actions: 'Welcome email + onboarding sequence', icon: '👋' },
    { name: 'Lead Nurturing', trigger: 'Downloaded ebook', actions: 'Follow-up emails + demo scheduling', icon: '🌱' },
    { name: 'Customer Feedback', trigger: '7 days after signup', actions: 'Survey + escalation if no response', icon: '💬' },
    { name: 'Cart Abandonment', trigger: 'Cart inactive 1h', actions: 'Reminder email + discount code', icon: '🛒' },
    { name: 'Support Triage', trigger: 'New support ticket', actions: 'Auto-categorize + assign agent', icon: '🎫' },
    { name: 'Review Request', trigger: 'Purchase + 14 days', actions: 'Review request email + incentive', icon: '⭐' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % AUTOMATION_EXAMPLE.steps.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <SEOHead
        title="Automation Builder Demo | Max OS Cloud"
        description="Build powerful automations with Max OS Cloud. No-code visual builder, real-time triggers, conditional logic. See live automation flow demo."
        url="/demo/automations"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Demos", item: "https://maxemployee.com/demo" },
            { "@type": "ListItem", position: 3, name: "Automations", item: "https://maxemployee.com/demo/automations" }
            ]
          }
        ]}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-purple-900/30 via-gray-900 to-gray-950 border-b border-purple-800/30">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="inline-block bg-purple-500/20 text-purple-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
            INTERACTIVE DEMO
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">{t('automations_demo.automation_builder_demo')}</h1>
          <p className="text-lg text-gray-400 max-w-2xl">
            Build powerful automations that handle customer interactions 24/7.
            No code required — just define triggers and actions.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Live automation flow */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">{t('automations_demo.automation_in_action')}</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-green-400 font-medium">{t('automations_demo.live_simulation')}</span>
              <span className="text-sm text-gray-600">— {AUTOMATION_EXAMPLE.name}</span>
            </div>

            {/* Flow visualization */}
            <div className="space-y-3">
              {AUTOMATION_EXAMPLE.steps.map((step, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-4 p-4 rounded-lg border transition-all duration-500 ${
                    idx === activeStep
                      ? 'border-purple-500 bg-purple-500/10 scale-[1.02]'
                      : 'border-gray-800 bg-gray-900/50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    idx === activeStep
                      ? 'bg-purple-500 text-white'
                      : idx < activeStep
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-800 text-gray-500'
                  }`}>
                    {idx < activeStep ? '✓' : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        step.type === 'trigger' ? 'bg-amber-500/20 text-amber-400' :
                        step.type === 'condition' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {step.type}
                      </span>
                      <span className="font-medium text-sm">{step.label}</span>
                    </div>
                    <p className="text-sm text-gray-500 font-mono">{step.detail}</p>
                  </div>
                  {idx === activeStep && (
                    <div className="text-purple-400 animate-pulse text-sm shrink-0">{t('automation.running')}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Rule builder preview */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">{t('automations_demo.rule_builder')}</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
            <div className="text-sm text-gray-500 mb-3">{t('automation.example_rule')}</div>
            <div className="bg-gray-950 rounded-lg p-4 font-mono text-sm space-y-2">
              <div className="text-purple-400">WHEN <span className="text-amber-400">{t('automation.message_contains')}</span>(<span className="text-green-400">'pedido'</span>)</div>
              <div className="text-purple-400">THEN <span className="text-blue-400">respond_with</span>(<span className="text-green-400">'order_status'</span>)</div>
              <div className="text-purple-400">AND <span className="text-blue-400">log_to</span>(<span className="text-green-400">'crm'</span>)</div>
            </div>
          </div>
        </div>

        {/* Templates */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">{t('automations_demo.automation_templates')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((tmpl) => (
              <div key={tmpl.name} className="bg-gray-900 border border-gray-800 rounded-xl p-5 hover:border-purple-500/50 transition">
                <div className="text-2xl mb-3">{tmpl.icon}</div>
                <h3 className="font-semibold mb-1">{tmpl.name}</h3>
                <p className="text-xs text-gray-500 mb-2">Trigger: {tmpl.trigger}</p>
                <p className="text-sm text-gray-400">{tmpl.actions}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">{t('automations_demo.automation_features')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: '⚡', title: 'Real-time Triggers', desc: 'React to customer messages, events, and API calls instantly' },
              { icon: '🔀', title: 'Conditional Logic', desc: 'IF/ELSE branches, loops, and multi-step decision trees' },
              { icon: '🔗', title: '300+ Integrations', desc: 'Connect to Stripe, Shopify, HubSpot, WhatsApp, and more' },
              { icon: '📈', title: 'Performance Analytics', desc: 'Track automation effectiveness with detailed metrics' },
              { icon: '🤖', title: 'AI-Powered', desc: 'LLM-based responses that understand context and intent' },
              { icon: '🛡️', title: 'Error Handling', desc: 'Automatic retries, fallbacks, and alerting for failures' },
            ].map((feature) => (
              <div key={feature.title} className="flex gap-4 bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="text-2xl">{feature.icon}</div>
                <div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-800/30 rounded-2xl p-10">
          <h2 className="text-2xl font-bold mb-3">{t('automations_demo.automate_your_customer_interactions')}</h2>
          <p className="text-gray-400 mb-6">Build your first automation in 5 minutes — no coding required.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="/pricing" className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-8 rounded-lg transition">
              Start Free Trial
            </a>
            <a href="/docs/automations" className="border border-gray-700 hover:border-gray-500 text-gray-300 font-medium py-3 px-8 rounded-lg transition">
              View Examples
            </a>
          </div>
        </div>
      </div>
      <Footer />

    </div>
  );
}

export default AutomationsDemo;
